/**
 * Issues API wrapper. Mirrors `apps/api/src/modules/issues` routes.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";

import { useAuth } from "../../providers/auth-provider.js";
import { analyticsAccessKey } from "../analytics/access-key.js";
import type { APIError } from "../http.js";
import { qk } from "../query-keys.js";

import { apiRequest } from "./_client.js";

export type IssueStatus =
  | "open"
  | "assigned"
  | "in_progress"
  | "resolved"
  | "verified"
  | "rejected"
  | "ignored"
  | "cancelled";

export type IssueType =
  | "oos"
  | "hidden_oos"
  | "price_mismatch"
  | "missing_price_tag"
  | "extra_price_tag"
  | "promo_missing"
  | "promo_wrong"
  | "posm_missing"
  | "planogram_mismatch"
  | "missing_facing"
  | "wrong_placement"
  | "extra_product"
  | "low_share_of_shelf"
  | "quality_issue"
  | "unknown";

export type SeverityLevel = "low" | "medium" | "high" | "critical";

export interface IssueInfo {
  id: string;
  storeId: string;
  storeName: string | null;
  reporterUserId: string | null;
  reporterEmail: string | null;
  assignedToUserId: string | null;
  assigneeEmail: string | null;
  issueType: IssueType;
  severity: SeverityLevel;
  status: IssueStatus;
  title: string;
  description: string | null;
  dueAt: string | null;
  resolvedAt: string | null;
  verifiedAt: string | null;
  resolvedByUserId: string | null;
  resolvedByEmail: string | null;
  verifiedByUserId: string | null;
  verifiedByEmail: string | null;
  resolutionNote: string | null;
  proofImageUrl: string | null;
  proofImageHash: string | null;
  captureId: string | null;
  sessionId: string | null;
  visionRunId: string | null;
  detectionId: string | null;
  cropImageHash: string | null;
  cropImageUrl: string | null;
  /** Human-readable location, resolved from the linked capture + detection. */
  fixtureCode: string | null;
  fixtureName: string | null;
  categoryName: string | null;
  zoneName: string | null;
  shelfLevel: number | null;
  shelfName?: string | null;
  shelfSource?: "physical" | "image" | null;
  capturedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface IssuesListResponse {
  items: IssueInfo[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ListIssuesQuery {
  page?: number;
  pageSize?: number;
  storeId?: string;
  status?: IssueStatus;
  statusGroup?: "actionable";
  sort?: "newest" | "urgency" | "updated";
  severity?: SeverityLevel;
  issueType?: IssueType;
  assignedToUserId?: string;
  captureId?: string;
  sessionId?: string;
  /** Case-insensitive substring match on issue title or description (server-side). */
  search?: string;
}

export interface CreateIssueRequest {
  storeId: string;
  assignedToUserId?: string | null;
  issueType?: IssueType;
  severity?: SeverityLevel;
  title: string;
  description?: string | null;
  dueAt?: string | null;
  metadata?: Record<string, unknown>;
  imageObjectKey?: string;
  imageSha256?: string;
}

export interface UploadIssueImageResponse {
  sha256: string;
  objectKey: string;
}

export interface UpdateIssueRequest {
  status?: IssueStatus;
  severity?: SeverityLevel;
  issueType?: IssueType;
  assignedToUserId?: string | null;
  title?: string;
  description?: string | null;
  dueAt?: string | null;
  metadata?: Record<string, unknown>;
  /** Resolution evidence (optional; typically sent with status:"resolved"). */
  resolutionNote?: string | null;
  resolutionProofImageUrl?: string;
  resolutionProofObjectKey?: string;
  resolutionProofSha256?: string;
}

export const issuesApi = {
  list: (query: ListIssuesQuery = {}, signal?: AbortSignal): Promise<IssuesListResponse> =>
    apiRequest({
      method: "GET",
      url: "/api/v2/issues",
      params: query as Record<string, string | number | boolean | null | undefined>,
      signal,
    }),

  get: (id: string, signal?: AbortSignal): Promise<IssueInfo> =>
    apiRequest({ method: "GET", url: `/api/v2/issues/${encodeURIComponent(id)}`, signal }),

  create: (body: CreateIssueRequest): Promise<IssueInfo> =>
    apiRequest({ method: "POST", url: "/api/v2/issues", data: body }),

  update: (id: string, patch: UpdateIssueRequest): Promise<IssueInfo> =>
    apiRequest({
      method: "PATCH",
      url: `/api/v2/issues/${encodeURIComponent(id)}`,
      data: patch,
    }),

  uploadImage: (file: File, kind: "crop" | "proof" = "crop"): Promise<UploadIssueImageResponse> => {
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("kind", kind);
    return apiRequest({ method: "POST", url: "/api/v2/issues/upload-image", body: form });
  },
};

export function uploadIssueImage(
  file: File,
  kind: "crop" | "proof" = "crop",
): Promise<UploadIssueImageResponse> {
  return issuesApi.uploadImage(file, kind);
}

export function useIssuesQuery(
  query: ListIssuesQuery = {},
  options?: Omit<UseQueryOptions<IssuesListResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<IssuesListResponse, APIError>({
    queryKey: qk.issues.list(query as Record<string, unknown>),
    queryFn: ({ signal }) => issuesApi.list(query, signal),
    retry: false,
    staleTime: 30_000,
    ...options,
  });
}

/** One capture's actionable work; totals come from the server, never a loaded-page count. */
export function useCaptureReviewIssues(captureId: string) {
  const { user } = useAuth();
  return useInfiniteQuery({
    queryKey: [...qk.issues.all, "capture-review", analyticsAccessKey(user), captureId],
    initialPageParam: 1,
    enabled: !!captureId,
    retry: false,
    queryFn: ({ pageParam, signal }) =>
      issuesApi.list(
        { captureId, statusGroup: "actionable", sort: "urgency", pageSize: 50, page: pageParam },
        signal,
      ),
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    refetchInterval: 30_000,
  });
}

export function useIssueQuery(
  id: string,
  options?: Omit<UseQueryOptions<IssueInfo, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<IssueInfo, APIError>({
    queryKey: qk.issues.detail(id),
    queryFn: ({ signal }) => issuesApi.get(id, signal),
    retry: false,
    enabled: id !== "",
    ...options,
  });
}

export function useCreateIssueMutation(
  options?: Omit<UseMutationOptions<IssueInfo, APIError, CreateIssueRequest>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<IssueInfo, APIError, CreateIssueRequest>({
    ...options,
    mutationFn: (body) => issuesApi.create(body),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.issues.all });
      void qc.invalidateQueries({ queryKey: ["analytics"] });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUpdateIssueMutation(
  options?: Omit<
    UseMutationOptions<IssueInfo, APIError, { id: string; patch: UpdateIssueRequest }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<IssueInfo, APIError, { id: string; patch: UpdateIssueRequest }>({
    ...options,
    mutationFn: ({ id, patch }) => issuesApi.update(id, patch),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.issues.all });
      void qc.invalidateQueries({ queryKey: ["analytics"] });
      void qc.invalidateQueries({ queryKey: qk.issues.detail(vars.id) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}
